import React, { useEffect, useMemo, useRef, useState } from 'react';
import getButtonStyleByColorScheme, { ColorScheme } from 'src/styles/functions/getButtonStyleByColorScheme';
import { StandardParagraphSize } from '../../typography/paragraph.styles';
import { DefaultLinkContainer, LinkContainerProps } from './LinkBranded.styles';
import {
  propagateQuerytoExternalLinks,
  urlIsExternal,
  applyGanalyticsTracking,
  trackButtonActivity,
} from 'src/functions/propagateQueryParams';
import { Statsig } from 'statsig-react';
import { windowIsLoaded } from 'src/functions/windowIsLoaded';
import { ButtonVariants } from 'src/api/models/Button';
import { CaptionSpan, CaptionSpanProps } from './LinkBrandedCaption.styles';

export interface LinkProps extends LinkContainerProps, CaptionSpanProps {
  id?: string;
  text: string;
  url?: string;
  colorScheme?: ColorScheme;
  standardSize?: StandardParagraphSize;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  colorSchemeVariant?: ButtonVariants;
  caption?: string;
  className?: string;
  // Additional statsig event for this link
  statsigEvent?: string;
}

const LinkBranded = (props: LinkProps): JSX.Element => {
  const {
    id,
    text,
    url,
    sizeSubLg,
    sizeMd,
    sizeXs,
    colorScheme = 'Sunshine',
    standardSize,
    icon,
    iconPosition = 'right',
    customBorderRadius,
    customBorderColor,
    colorSchemeVariant = 'primary',
    customFontWeight,
    caption,
    captionColor = null,
    statsigEvent = null,
  } = props;

  const size = props.size ?? StandardParagraphSize[standardSize]?.desktop;
  const sizeLg = props.sizeLg ?? StandardParagraphSize[standardSize]?.lg;
  const sizeSm = props.sizeSm ?? StandardParagraphSize[standardSize]?.sm;

  const sizeProps = {
    size,
    sizeLg,
    sizeSm,
    sizeMd,
    sizeSubLg,
    sizeXs,
  };

  const buttonRef = useRef<HTMLAnchorElement>();
  const mountedComponentRef = useRef(true);
  const [queryPropagatedUrl, updateHref] = useState(url);

  useEffect(() => {
    windowIsLoaded().then((): void => {
      if (!mountedComponentRef.current) return null;

      updateHref(propagateQuerytoExternalLinks(url));
    });
  }, [url]);

  useEffect(() => {
    return () => {
      mountedComponentRef.current = false;
    };
  }, []);

  const onClickToScroll = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    document.querySelector(url).scrollIntoView({
      behavior: 'smooth',
    });
  };

  // Smooth scroll only works when url is ID type
  const isScrollEvent = url.includes('#') && !urlIsExternal(url);
  const navigationProp = isScrollEvent ? {} : { href: queryPropagatedUrl || '#' };
  const targetProp = useMemo(() => {
    return urlIsExternal(url) ? { target: '_blank', rel: 'noreferrer' } : {};
  }, [url]);

  const linkColors = useMemo(
    () => getButtonStyleByColorScheme(colorScheme, colorSchemeVariant),
    [colorScheme, colorSchemeVariant]
  );

  // Extend onClick listener for Trackers, etc.
  const trackOnClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    const baseOnClick = isScrollEvent ? onClickToScroll : applyGanalyticsTracking(queryPropagatedUrl, url);
    baseOnClick(e);
    // >> Additional trackers
    trackButtonActivity(buttonRef.current, text);
    if (statsigEvent) {
      Statsig.logEvent(statsigEvent, 1);
    }
    return true;
  };

  return (
    <DefaultLinkContainer
      id={id}
      {...sizeProps}
      {...navigationProp}
      {...targetProp}
      {...linkColors}
      ref={buttonRef}
      onClick={trackOnClick}
      customBorderRadius={customBorderRadius}
      customBorderColor={customBorderColor}
      customFontWeight={customFontWeight}
      className={`link-branded ${props.className ?? ''}`}
    >
      {icon && iconPosition === 'left' && <span className='icon-left'>{icon}</span>}
      <span className='link-branded-text'>{text}</span>
      {icon && iconPosition === 'right' && <span className='icon-right'>{icon}</span>}
      {caption ? (
        <CaptionSpan
          className='button-caption'
          captionColor={captionColor}
        >
          {caption}
        </CaptionSpan>
      ) : null}
    </DefaultLinkContainer>
  );
};

export default LinkBranded;
